import { Dispatch, SetStateAction } from "react";
import _ from "lodash";

import useAccountMetadata from "@/hooks/useAccountMetadata";

import { UserEnvironmentSelectorBase } from "../UserEnvironmentSelectorBase";

type EnvSelectionMap = Record<string, boolean>;

type UserEnvironmentSelectorInviteModalProps = {
  inviteSelectedEnvs: EnvSelectionMap | null;
  setInviteSelectedEnvs: Dispatch<SetStateAction<EnvSelectionMap | null>>;
};

export const UserEnvironmentSelectorInviteModal = ({
  inviteSelectedEnvs,
  setInviteSelectedEnvs,
}: UserEnvironmentSelectorInviteModalProps) => {
  const accountMetadata = useAccountMetadata();
  const projects = accountMetadata.projects.filter(
    (project) => project.environments.length,
  );

  const allEnvIDs = (): string[] =>
    _.flatten(
      projects.map((project) =>
        project.environments
          .map((env) => env.ID)
          .filter((id): id is string => id !== null),
      ),
    );

  const getHasAccess = (_projID: string, envID: string) => {
    if (!inviteSelectedEnvs) {
      return true;
    }
    return !!inviteSelectedEnvs[envID];
  };

  const updateAccess = (
    projID: string | null,
    envID: string | null,
    hasAccess: boolean,
  ) => {
    let updatedEnvIDs: string[] = [];
    if (projID && envID) {
      updatedEnvIDs = [envID];
    } else if (projID) {
      const project = projects.find((project) => project.ID === projID);
      if (project) {
        updatedEnvIDs = project.environments
          .map((env) => env.ID)
          .filter((id): id is string => id !== null);
      }
    } else {
      updatedEnvIDs = allEnvIDs();
    }

    setInviteSelectedEnvs((prevEnvs) => {
      let envs: EnvSelectionMap;
      if (prevEnvs) {
        envs = { ...prevEnvs };
      } else {
        envs = {};
        for (const envID of allEnvIDs()) {
          envs[envID] = true;
        }
      }

      for (const envID of updatedEnvIDs) {
        envs[envID] = hasAccess;
      }
      return envs;
    });
  };

  return (
    <UserEnvironmentSelectorBase
      getHasAccess={getHasAccess}
      updateAccess={updateAccess}
      enableToggle={true}
      showAllProjectsEnvs={true}
      showProjectCheckbox={true}
    />
  );
};
